import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { api } from '@/lib/api/client';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/stores/auth';
import { AdminLayout } from '@/components/layouts/admin-layout';
import FilterBar from '@/components/common/filter-bar';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Check,
  X
} from 'lucide-react';

// Access request interface
interface AccessRequest {
  id: number;
  userId: number;
  streamId: number;
  status: 'pending' | 'approved' | 'rejected';
  reason?: string;
  reviewedBy?: number;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  // Joined user info
  userName?: string;
  userEmail?: string;
  // Joined stream info
  streamTitle?: string;
}

interface AccessRequestsData {
  requests: AccessRequest[];
  total: number;
  page: number;
  pageSize: number;
}

interface ReviewResponseData {
  message: string;
}

export default function AdminRequestsPage() {
  const { isInitialized } = useAuthStore();
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [statusView, setStatusView] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const pageSize = 20;

  // Filter state managed by FilterBar
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});

  useEffect(() => {
    if (!isInitialized) return;
    fetchRequests();
  }, [isInitialized, page, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...filters
      });

      const response = await api.get<AccessRequestsData>(`/api/admin/access-requests?${params}`);

      if (response.success && response.data) {
        setRequests(Array.isArray(response.data.requests) ? response.data.requests : []);
        setTotal(response.data.total || 0);
      } else {
        setError(response.error?.message || 'Failed to load access requests');
      }
    } catch (err) {
      console.error('Error fetching access requests:', err);
      setError('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (requestId: number, action: 'approve' | 'reject') => {
    try {
      setProcessingId(requestId);

      const response = await api.put<ReviewResponseData>(`/api/admin/access-requests/${requestId}/review`, { action });

      if (response.success) {
        // Update the local state
        setRequests(prev =>
          prev.map(req =>
            req.id === requestId
              ? {
                ...req,
                status: action === 'approve' ? 'approved' : 'rejected',
                reviewedAt: new Date().toISOString()
              }
              : req
          )
        );
        toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      } else {
        toast.error(response.error?.message || `Failed to ${action} request`);
        setError(response.error?.message || `Failed to ${action} request`);
      }
    } catch (err) {
      console.error(`Error ${action}ing request:`, err);
      toast.error(`Failed to ${action} request`);
      setError(`Failed to ${action} request`);
    } finally {
      setProcessingId(null);
    }
  };

  const filteredRequests = requests.filter(req => {
    const search = (filters.search as string) || '';
    const type = (filters.type as string) || '';

    const matchesSearch = search === '' ||
      req.userName?.toLowerCase().includes(search.toLowerCase()) ||
      req.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
      req.streamTitle?.toLowerCase().includes(search.toLowerCase()) ||
      req.reason?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusView === 'all' || req.status === statusView;
    const matchesType = type === '' || type === 'all' || true; // Type filter placeholder

    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      'pending': 'secondary',
      'approved': 'default',
      'rejected': 'destructive'
    };

    const icons = {
      'pending': <AlertCircle className="h-3 w-3" />,
      'approved': <CheckCircle className="h-3 w-3" />,
      'rejected': <XCircle className="h-3 w-3" />
    };

    return (
      <Badge variant={variants[status] || 'outline'} className="flex items-center gap-1">
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
      <Tabs value={statusView} onValueChange={(value) => setStatusView(value as typeof statusView)}>
        <TabsList className="inline-flex h-auto w-auto flex-wrap gap-1">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="p-2 sm:p-3">
        <FilterBar
          filters={[
            {
              id: 'search',
              label: 'Search',
              type: 'search',
              placeholder: 'Search requests by user, stream, or reason...'
            },
            {
              id: 'type',
              label: 'Type',
              type: 'select',
              options: [
                { value: 'all', label: 'All Types' },
                { value: 'stream_access', label: 'Stream Access' },
                { value: 'invite_request', label: 'Invite Request' }
              ],
              placeholder: 'All Types'
            }
          ]}
          activeFilters={filters}
          onFilterChange={setFilters}
        />
      </div>

      {/* Requests Table */}
      <section className="space-y-3">
        <div className="px-4 text-sm text-muted-foreground sm:px-6">
          {filteredRequests.length} access requests
        </div>
        <div className="px-4 sm:px-6">
          {filteredRequests.length === 0 && !error ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No access requests found</h3>
              <p className="text-muted-foreground">
                {Object.keys(filters).length > 0
                  ? 'Try adjusting your search or filters'
                  : 'Access requests will appear here when users request stream access'
                }
              </p>
            </div>
          ) : !error ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Stream</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Reviewed</TableHead>
                  <TableHead className="w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src="" />
                          <AvatarFallback>
                            {request.userName?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{request.userName || 'Unknown User'}</p>
                          <p className="text-sm text-muted-foreground">{request.userEmail}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{request.streamTitle || `Stream #${request.streamId}`}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm max-w-xs truncate">
                        {request.reason || '-'}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{formatDate(request.createdAt)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {request.reviewedAt ? (
                        <span className="text-sm">{formatDate(request.reviewedAt)}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not reviewed</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {request.status === 'pending' ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleReview(request.id, 'approve')}
                            disabled={processingId === request.id}
                          >
                            {processingId === request.id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleReview(request.id, 'reject')}
                            disabled={processingId === request.id}
                          >
                            {processingId === request.id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {request.status === 'approved' ? 'Approved' : 'Rejected'}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </div>
      </section>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            Page {page} of {Math.ceil(total / pageSize)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
            disabled={page === Math.ceil(total / pageSize) || loading}
          >
            Next
          </Button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <p className="text-destructive text-center">{error}</p>
            <Button
              variant="outline"
              onClick={fetchRequests}
              className="mt-2 w-full"
              disabled={loading}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
